import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type MaintenanceMode = "off" | "read_only" | "full";

export interface MaintenanceConfig {
  tenant_id: string;
  mode: MaintenanceMode;
  message: string;
  eta: string | null;
}

export const DEFAULT_MAINTENANCE_CONFIG: MaintenanceConfig = {
  tenant_id: "global",
  mode: "off",
  message: "We're upgrading. You can browse, but changes are paused.",
  eta: null,
};

export const maintenanceConfigKey = ["maintenance_config", "global"] as const;

export function useMaintenance() {
  const { data, isLoading } = useQuery({
    queryKey: maintenanceConfigKey,
    queryFn: async (): Promise<MaintenanceConfig> => {
      const { data: row, error } = await supabase
        .from("maintenance_config")
        .select("tenant_id, mode, message, eta")
        .eq("tenant_id", "global")
        .maybeSingle();
      if (error) throw error;
      if (!row) return DEFAULT_MAINTENANCE_CONFIG;
      return {
        tenant_id: row.tenant_id,
        mode: (row.mode as MaintenanceMode) ?? "off",
        message: row.message || DEFAULT_MAINTENANCE_CONFIG.message,
        eta: row.eta ? new Date(row.eta).toISOString() : null,
      };
    },
    // Every 30 minutes is enough to notice a freeze (the database refuses
    // writes on its own under read_only). Every 5 minutes plus every tab
    // focus was 950 reads a day (Oct 25) for a switch that is almost always
    // off; the banner is a courtesy, not the lock.
    staleTime: 10 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const config = data ?? DEFAULT_MAINTENANCE_CONFIG;

  return {
    config,
    isLoading,
    active: config.mode !== "off",
  };
}
